package com.example.mystybag.appCore.views.addMerchantLocation

import androidx.compose.foundation.Image
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.example.mystybag.appCore.logic.addMerchantLocation.MENU_PHOTO_ASPECT_RATIO
import com.example.mystybag.appCore.logic.addMerchantLocation.MerchantRegistrationMenuPhotosViewModel
import com.example.mystybag.appCore.logic.addMerchantLocation.MAX_MENU_PHOTOS
import com.example.mystybag.appCore.logic.addMerchantLocation.MENU_PHOTOS_GRID_COLUMNS
import com.example.mystybag.common.ui.components.custom.ImageDescriptionView
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.common.views.CommonBottomSheetFactory
import com.example.mystybag.core.model.ImageDescription
import com.example.mystybag.core.model.IconDescription
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.ColorDefines
import com.example.mystybag.core.views.SharedColor
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun MerchantRegistrationMenuPhotosView(viewModel: MerchantRegistrationMenuPhotosViewModel) {
    BottomSheetUIView(
        viewModel = viewModel,
        modifier = Modifier.fillMaxSize(),
        bottomSheetFactory = { CommonBottomSheetFactory(it) }
    ) { state ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = p16)
        ) {
            Spacer(modifier = Modifier.height(p16))

            Texts.DisplayMedium(
                text = stringResource(SR.strings.menu_photos),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(p8))

            Texts.BodyMedium(
                text = stringResource(SR.strings.menu_photos_up_to_10),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(p16))

            // Grid: existing photos + add slot (if < 10)
            val items = state.value.menuPhotoFileNames
            val canAdd = items.size < MAX_MENU_PHOTOS
            val allSlots: List<Pair<Int, String?>> = items.mapIndexed { index, fileName -> index to fileName } + if (canAdd) listOf(-1 to null) else emptyList()
            val rows = allSlots.chunked(MENU_PHOTOS_GRID_COLUMNS)

            Column(verticalArrangement = Arrangement.spacedBy(p6)) {
                rows.forEach { rowSlots ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(p6)
                    ) {
                        rowSlots.forEach { slot ->
                            val index = slot.first
                            val fileName = slot.second
                            val isAddSlot = fileName == null
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .aspectRatio(MENU_PHOTO_ASPECT_RATIO)
                                    .clip(RoundedCornerShape(if (isAddSlot) p16 else p8))
                                    .then(
                                        if (isAddSlot) {
                                            Modifier
                                        } else {
                                            Modifier.border(p1, AppTheme.current.colors.outline, RoundedCornerShape(p8))
                                        }
                                    )
                            ) {
                                if (fileName != null) {
                                    val imagePath = com.example.mystybag.cropImage.expected.getTempImagePath(fileName)
                                    ImageDescriptionView(
                                        modifier = Modifier.fillMaxSize(),
                                        image = ImageDescription(link = imagePath, icon = null),
                                        contentScale = ContentScale.Crop,
                                    )
                                    Box(
                                        modifier = Modifier
                                            .align(Alignment.TopEnd)
                                            .padding(p4)
                                            .size(32.dp)
                                            .clip(CircleShape)
                                            .background(AppTheme.current.colors.error)
                                            .clickable { viewModel.removeMenuPhotoAt(index) },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Image(
                                            painter = painterResource(SRC.images.mol_trash_icon),
                                            contentDescription = stringResource(SR.strings.remove_photo),
                                            modifier = Modifier.size(16.dp)
                                        )
                                    }
                                } else {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxSize()
                                            .clip(RoundedCornerShape(p16))
                                            .background(AppTheme.current.colors.primaryContainer)
                                            .clickable { viewModel.onAddMenuPhotoClicked() },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Column(
                                            horizontalAlignment = Alignment.CenterHorizontally,
                                            verticalArrangement = Arrangement.Center,
                                            modifier = Modifier.padding(horizontal = p4, vertical = p4)
                                        ) {
                                            ImageDescriptionView(
                                                modifier = Modifier.size(p24),
                                                image = ImageDescription(
                                                    link = null,
                                                    icon = IconDescription(
                                                        resource = SRC.images.camera_1,
                                                        imageSize = p24,
                                                        background = SharedColor(color = ColorDefines.Surface, alpha = 0f)
                                                    )
                                                )
                                            )
                                            Spacer(modifier = Modifier.height(p4))
                                            Texts.LabelSmall(
                                                text = stringResource(SR.strings.add_menu_photo),
                                                color = AppTheme.current.colors.primary,
                                                textAlign = TextAlign.Center,
                                                maxLines = 3
                                            )
                                        }
                                    }
                                }
                            }
                        }
                        repeat(MENU_PHOTOS_GRID_COLUMNS - rowSlots.size) {
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(p8))

            if (state.value.errorMessage != null) {
                Texts.BodyMedium(
                    text = state.value.errorMessage ?: "",
                    color = AppTheme.current.colors.error,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(p8))
            }

            if (state.value.isLoading) {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
                Spacer(modifier = Modifier.height(p16))
            }

            DefaultButton(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = p16),
                type = DefaultButtonType.BLUE,
                caption = stringResource(SR.strings.continue_camel_case),
                callback = viewModel::onContinue,
                enabled = !state.value.isLoading
            )
        }
    }
}
